import { getNearestSupportedLanguage } from '@splitfiesta/i18n';

/**
 * Service for browser detection and locale mapping
 */

export interface BrowserDefaults {
  language: string;
  currency: string;
}

// Map of locale patterns to currencies
const localeToCurrency: Record<string, string> = {
  // Americas
  'en-US': 'USD',
  'es-MX': 'MXN',
  'es-CO': 'COP',
  'es-AR': 'ARS',
  'es-CL': 'CLP',
  'es-PE': 'PEN',
  'es-VE': 'VES',
  'es-UY': 'UYU',
  'es-PY': 'PYG',
  'es-BO': 'BOB',
  'es-EC': 'USD', // Ecuador uses USD
  'es-CR': 'CRC',
  'es-GT': 'GTQ',
  'es-HN': 'HNL',
  'es-NI': 'NIO',
  'es-PA': 'PAB',
  'es-DO': 'DOP',
  'es-PR': 'USD', // Puerto Rico uses USD
  'pt-BR': 'BRL',
  'en-CA': 'CAD',
  'fr-CA': 'CAD',

  // Europe
  'en-GB': 'GBP',
  'de-DE': 'EUR',
  'fr-FR': 'EUR',
  'es-ES': 'EUR',
  'it-IT': 'EUR',
  'nl-NL': 'EUR',
  'pt-PT': 'EUR',
  'de-AT': 'EUR',
  'fr-BE': 'EUR',
  'nl-BE': 'EUR',
  'fi-FI': 'EUR',
  'sv-SE': 'SEK',
  'da-DK': 'DKK',
  'nb-NO': 'NOK',
  'nn-NO': 'NOK',
  'is-IS': 'ISK',
  'pl-PL': 'PLN',
  'cs-CZ': 'CZK',
  'sk-SK': 'EUR',
  'hu-HU': 'HUF',
  'ro-RO': 'RON',
  'bg-BG': 'BGN',
  'hr-HR': 'EUR',
  'sl-SI': 'EUR',
  'et-EE': 'EUR',
  'lv-LV': 'EUR',
  'lt-LT': 'EUR',
  'mt-MT': 'EUR',
  'cy-CY': 'EUR',
  'el-GR': 'EUR',
  'tr-TR': 'TRY',
  'ru-RU': 'RUB',
  'uk-UA': 'UAH',
  'be-BY': 'BYN',

  // Asia Pacific
  'zh-CN': 'CNY',
  'zh-TW': 'TWD',
  'zh-HK': 'HKD',
  'ja-JP': 'JPY',
  'ko-KR': 'KRW',
  'en-IN': 'INR',
  'hi-IN': 'INR',
  'en-SG': 'SGD',
  'zh-SG': 'SGD',
  'ms-MY': 'MYR',
  'en-MY': 'MYR',
  'th-TH': 'THB',
  'vi-VN': 'VND',
  'id-ID': 'IDR',
  'tl-PH': 'PHP',
  'en-PH': 'PHP',
  'en-AU': 'AUD',
  'en-NZ': 'NZD',

  // Middle East & Africa
  'ar-SA': 'SAR',
  'ar-AE': 'AED',
  'ar-EG': 'EGP',
  'he-IL': 'ILS',
  'en-ZA': 'ZAR',
  'ar-DZ': 'DZD',
  'ar-MA': 'MAD',
  'en-NG': 'NGN',
  'en-KE': 'KES',
};

class BrowserDetectionService {
  /**
   * Detect user's preferred language from browser settings
   */
  detectLanguage(): string {
    if (typeof window === 'undefined') return 'en';

    return getNearestSupportedLanguage(navigator.language);
  }

  /**
   * Get the best locale with region information from browser
   * Tries multiple sources: navigator.languages, Intl.DateTimeFormat, navigator.language
   */
  private getBestLocaleWithRegion(): string {
    // Try navigator.languages first (array of preferred locales)
    if (navigator.languages && navigator.languages.length > 0) {
      // Find the first locale with a region code (e.g., "en-CA", "es-MX")
      const localeWithRegion = navigator.languages.find(
        locale => locale.includes('-') || locale.includes('_')
      );
      if (localeWithRegion) {
        return localeWithRegion.replace('_', '-');
      }
    }

    // Try Intl.DateTimeFormat to get system locale with region
    try {
      const resolved = Intl.DateTimeFormat().resolvedOptions();
      if (resolved.locale && resolved.locale.includes('-')) {
        return resolved.locale;
      }
    } catch {
      // Ignore errors
    }

    // Fall back to navigator.language
    return navigator.language;
  }

  /**
   * Detect user's currency based on locale
   */
  detectCurrency(supportedCurrencies: string[]): string {
    if (typeof window === 'undefined') return 'USD';

    // Get the best locale with region info
    const localeWithRegion = this.getBestLocaleWithRegion();
    const fullLocale = navigator.language;

    // First, try exact match with locale that has region
    if (localeToCurrency[localeWithRegion]) {
      const currency = localeToCurrency[localeWithRegion];
      if (supportedCurrencies.includes(currency)) {
        return currency;
      }
    }

    // Try navigator.language as fallback
    if (localeWithRegion !== fullLocale && localeToCurrency[fullLocale]) {
      const currency = localeToCurrency[fullLocale];
      if (supportedCurrencies.includes(currency)) {
        return currency;
      }
    }

    // Try all locales in navigator.languages
    if (navigator.languages) {
      for (const locale of navigator.languages) {
        const normalizedLocale = locale.replace('_', '-');
        if (localeToCurrency[normalizedLocale]) {
          const currency = localeToCurrency[normalizedLocale];
          if (supportedCurrencies.includes(currency)) {
            return currency;
          }
        }
      }
    }

    // Try using Intl.NumberFormat to detect currency
    try {
      const numberFormat = new Intl.NumberFormat(localeWithRegion, {
        style: 'currency',
        currency: 'USD', // fallback currency for detection
      });

      const formatToParts = numberFormat.formatToParts(123.45);
      const currencyPart = formatToParts.find(part => part.type === 'currency');

      if (currencyPart && supportedCurrencies.includes(currencyPart.value)) {
        return currencyPart.value;
      }
    } catch {
      // Currency detection failed, fallback to language-based detection
    }

    // Fallback based on language
    const language = this.detectLanguage();
    const languageFallbacks: Record<string, string> = {
      en: 'USD',
      es: 'EUR',
      fr: 'EUR',
      de: 'EUR',
      pt: 'BRL',
    };

    const fallbackCurrency = languageFallbacks[language] || 'USD';
    return supportedCurrencies.includes(fallbackCurrency)
      ? fallbackCurrency
      : 'USD';
  }

  /**
   * Detect all browser defaults at once
   */
  detectBrowserDefaults(supportedCurrencies: string[]): BrowserDefaults {
    return {
      language: this.detectLanguage(),
      currency: this.detectCurrency(supportedCurrencies),
    };
  }

  /**
   * Check if running in browser environment
   */
  isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * Get locale to currency mapping
   */
  getLocaleToCurrencyMap(): Record<string, string> {
    return { ...localeToCurrency };
  }

  /**
   * Get currency for a specific locale
   */
  getCurrencyForLocale(locale: string): string | undefined {
    return localeToCurrency[locale];
  }
}

export const browserDetectionService = new BrowserDetectionService();
